import { LoadingDots } from '@advanza/ui'
import { AlertCircle, ChevronDown, ChevronRight, Hourglass01 } from '@untitledui/icons'
import clsx from 'clsx'
import { formatDuration, runStatusMeta, STEP_LABELS } from 'components/billing/billingRun/runUi'
import { Card } from 'components/billing/debtorNew/ui'
import { format } from 'date-fns'
import { BillingRunStep, useBillingRunDetail } from 'hooks/billingRunHooks'
import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'

const COL_COUNT = 8

/**
 * The persistent, filterable failure report of one run — replaces the
 * one-time JSON popup of the old flow.
 */
const RunErrors = ({ runId }: { runId: number }) => {
    const { errors, countsByReason, isFetching } = useBillingRunDetail(runId)
    const [reasonFilter, setReasonFilter] = useState<string | null>(null)

    if (isFetching) {
        return <LoadingDots />
    }
    if (errors.length === 0) {
        return <p className="text-md text-neutral-400">No errors recorded for this run.</p>
    }

    const visibleErrors = reasonFilter
        ? errors.filter((error) => error.reason_code === reasonFilter)
        : errors

    return (
        <div>
            <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold uppercase tracking-wide text-neutral-400">
                    Filter by reason
                </span>
                {Object.entries(countsByReason).map(([reason, count]) => (
                    <button
                        key={reason}
                        type="button"
                        onClick={() => setReasonFilter(reasonFilter === reason ? null : reason)}
                        className={clsx(
                            'rounded-full px-2.5 py-1 text-sm font-medium transition',
                            reasonFilter === reason
                                ? 'bg-primary text-white'
                                : 'bg-white text-neutral-600 shadow-xs ring-1 ring-neutral-200 hover:bg-neutral-50',
                        )}>
                        {reason.replace(/_/g, ' ')} · {count}
                    </button>
                ))}
            </div>
            <div className="overflow-hidden rounded-lg bg-white ring-1 ring-neutral-200">
                <table className="w-full text-md">
                    <thead>
                        <tr className="border-b border-neutral-100 text-left text-sm uppercase tracking-wide text-neutral-400">
                            <th className="px-3 py-2">Provider</th>
                            <th className="px-3 py-2">Invoice</th>
                            <th className="px-3 py-2">Reason</th>
                            <th className="px-3 py-2">Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleErrors.map((error) => (
                            <tr
                                key={error.billing_run_step_error_id}
                                className="border-t border-neutral-50">
                                <td className="px-3 py-2">
                                    {error.service_provider_id ? (
                                        <Link
                                            to={`/service/providers/provider/${error.service_provider_id}`}
                                            target="_blank"
                                            className="text-primary-600 hover:underline">
                                            {error.provider?.business_name ||
                                                `#${error.service_provider_id}`}
                                        </Link>
                                    ) : (
                                        '—'
                                    )}
                                </td>
                                <td className="px-3 py-2 tabular-nums">
                                    {error.invoice?.invoice_nr || '—'}
                                </td>
                                <td className="px-3 py-2">
                                    <span className="whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-sm font-medium text-red-700">
                                        {error.reason_code.replace(/_/g, ' ')}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-sm text-neutral-500">
                                    {error.message}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

interface RunHistoryProps {
    runs: BillingRunStep[]
    onAbort: (runId: number) => void
    expandedRunId: number | null
    onToggleExpand: (runId: number | null) => void
}

const RunHistory = ({ runs, onAbort, expandedRunId, onToggleExpand }: RunHistoryProps) => {
    if (runs.length === 0) {
        return (
            <Card className="flex flex-col items-center gap-2 px-4 py-10 text-center">
                <Hourglass01 className="h-6 w-6 text-neutral-300" />
                <p className="text-md font-medium text-neutral-500">No runs yet</p>
                <p className="text-sm text-neutral-400">
                    Every started step shows up here with its progress and error report — also
                    months later.
                </p>
            </Card>
        )
    }

    return (
        <Card className="overflow-x-auto">
            <table className="w-full text-md">
                <thead>
                    <tr className="border-b border-neutral-200 text-left text-sm uppercase tracking-wide text-neutral-400">
                        <th className="px-4 py-2.5">Step</th>
                        <th className="px-4 py-2.5">Month</th>
                        <th className="px-4 py-2.5">Status</th>
                        <th className="px-4 py-2.5 text-right">Progress</th>
                        <th className="px-4 py-2.5 text-right">Errors</th>
                        <th className="px-4 py-2.5">Started</th>
                        <th className="px-4 py-2.5">Duration</th>
                        <th className="px-4 py-2.5">By</th>
                    </tr>
                </thead>
                <tbody>
                    {runs.map((run) => {
                        const isExpanded = expandedRunId === run.billing_run_step_id
                        const status = runStatusMeta(run)
                        const errorCount = run.failed + run.skipped

                        return (
                            <Fragment key={run.billing_run_step_id}>
                                <tr
                                    onClick={() =>
                                        onToggleExpand(isExpanded ? null : run.billing_run_step_id)
                                    }
                                    className={clsx(
                                        'cursor-pointer border-t border-neutral-100 transition hover:bg-neutral-50',
                                        isExpanded && 'bg-neutral-50',
                                    )}>
                                    <td className="px-4 py-2.5">
                                        <span className="flex items-center gap-1.5 font-medium text-dark">
                                            {isExpanded ? (
                                                <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
                                            ) : (
                                                <ChevronRight className="h-3.5 w-3.5 text-neutral-300" />
                                            )}
                                            {STEP_LABELS[run.step]}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 tabular-nums text-neutral-600">
                                        {run.about_month.slice(0, 7)}
                                        <span className="ml-1.5 text-sm text-neutral-400">
                                            {run.countries}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <span
                                            className={clsx(
                                                'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-sm font-medium',
                                                status.pill,
                                            )}>
                                            <span
                                                className={clsx(
                                                    'h-1.5 w-1.5 rounded-full',
                                                    status.dot,
                                                    run.status === 'running' &&
                                                        !run.is_stale &&
                                                        'animate-pulse',
                                                )}
                                            />
                                            {status.label}
                                        </span>
                                        {run.status === 'running' && (
                                            <button
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    if (
                                                        window.confirm(
                                                            'Abort this run? Work already done stays; re-running picks up the rest.',
                                                        )
                                                    ) {
                                                        onAbort(run.billing_run_step_id)
                                                    }
                                                }}
                                                className="ml-2 text-sm font-medium text-red-600 hover:underline">
                                                abort
                                            </button>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-neutral-600">
                                        {run.processed} / {run.total}
                                        <span className="ml-1.5 text-sm text-green-600">
                                            {run.succeeded} ok
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        {errorCount > 0 ? (
                                            <span
                                                className={clsx(
                                                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-sm font-medium tabular-nums',
                                                    run.failed > 0
                                                        ? 'bg-red-50 text-red-700'
                                                        : 'bg-neutral-100 text-neutral-600',
                                                )}>
                                                <AlertCircle className="h-3 w-3" />
                                                {run.failed > 0 && `${run.failed} failed`}
                                                {run.failed > 0 && run.skipped > 0 && ' · '}
                                                {run.skipped > 0 && `${run.skipped} skipped`}
                                            </span>
                                        ) : (
                                            <span className="text-sm text-neutral-300">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 tabular-nums text-neutral-600">
                                        {format(new Date(run.started), 'd MMM yyyy HH:mm')}
                                    </td>
                                    <td className="px-4 py-2.5 tabular-nums text-neutral-600">
                                        {formatDuration(run.started, run.finished) || '—'}
                                    </td>
                                    <td className="px-4 py-2.5 text-neutral-600">
                                        {run.triggered_by_user?.username || '—'}
                                    </td>
                                </tr>
                                {isExpanded && (
                                    <tr className="border-t border-neutral-100 bg-neutral-50">
                                        <td colSpan={COL_COUNT} className="px-4 py-4">
                                            <RunErrors runId={run.billing_run_step_id} />
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        )
                    })}
                </tbody>
            </table>
        </Card>
    )
}

export default RunHistory
